import { defineStore } from 'pinia';
import * as authService from '@/services/authService';
import { accountApi } from '@/services/accountApi';

export const useUserStore = defineStore('user', {
  state: () => ({
    user: null,    // parent or student
    role: null,    // 'parent' | 'student'
    session: null, // mirrors authService session: { uid, name, email, plan, onboardingComplete, subscription? }
  }),

  getters: {
    /**
     * The plan the account currently has access to.
     * Set by the API via setSubscription() — the backend is the single source
     * of truth and accounts for cancellations, renewsAt, grace periods etc.
     * Falls back to the raw session.plan if syncAccount hasn't run yet.
     */
    effectivePlan(state) {
      return state.session?.effectivePlan || state.session?.plan || 'free';
    },
  },

  actions: {
    login(user, role) {
      this.user = user;
      this.role = role;
    },

    logout() {
      authService.logout();
      this.user = null;
      this.role = null;
      this.session = null;
    },

    /** Restore session on app boot (call before app.mount). */
    initFromStorage() {
      this.session = authService.getSession();
    },

    /** Update the chosen plan in localStorage and store state. */
    setPlan(plan) {
      if (this.session?.uid) {
        authService.setPlan(this.session.uid, plan);
        this.session = { ...this.session, plan };
      }
    },

    /**
     * Refresh subscription state from the API in the background.
     * Called on app boot so that stale localStorage sessions are corrected
     * (e.g. renewsAt has passed since last visit).
     * Non-blocking — never throws.
     */
    async syncAccount() {
      const uid = this.session?.uid;
      if (!uid) return;
      try {
        const { account } = await accountApi.getAccount(uid);
        if (account) {
          if (account.subscription) {
            authService.setSubscription(uid, account.subscription);
          }
          // onboardingComplete is the backend's source of truth
          if (account.onboardingComplete) {
            authService.markOnboardingComplete(uid);
          } else {
            authService.resetOnboarding(uid);
          }
          this.session = authService.getSession();
        }
      } catch {
        // Non-critical — continue with the cached session value
      }
    },
  },
});
